import React, { useEffect, useMemo, useState } from "react";
import { membershipApi } from "@/api/membershipClient";

/**
 * MembershipContext provides the active tier + feature matrix to the public app.
 * Tier can be overridden via the `?tier=executive` query parameter (demo mode)
 * or stored in localStorage as `oracle.demo.tier`. AdminOne pages do not depend
 * on this context — they use the AdminOne membership client directly.
 */
const LS_KEY = "oracle.demo.tier";
const CTX_DEFAULT = {
  tier: "free",
  tierLabel: "Free",
  features: [],
  allowed: () => false,
  requiredTier: () => "free",
  setDemoTier: () => {},
  isLoading: true,
  tiers: ["free", "professional", "executive", "enterprise"],
};
const Ctx = React.createContext(CTX_DEFAULT);

export function useMembership() { return React.useContext(Ctx); }

function readInitialTier() {
  try {
    const u = new URL(window.location.href);
    const qp = u.searchParams.get("tier");
    if (qp) return qp;
    const ls = window.localStorage.getItem(LS_KEY);
    if (ls) return ls;
  } catch (_) { /* ignore */ }
  return null;
}

export function MembershipProvider({ children }) {
  const [tier, setTier] = useState(readInitialTier() || "free");
  const [profile, setProfile] = useState(null);
  const [tiers, setTiers] = useState(["free", "professional", "executive", "enterprise"]);
  const [loading, setLoading] = useState(true);
  // Phase 30A — re-fetch /me whenever the authenticated session token changes
  // (login / logout) so the gating UI reflects the real persisted tier.
  const [authTick, setAuthTick] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onStorage = (e) => {
      if (!e || !e.key) return;
      if (e.key === "carelogix_member_token" || e.key === "carelogix_member_user") {
        setAuthTick((t) => t + 1);
      }
    };
    const onCustom = () => setAuthTick((t) => t + 1);
    window.addEventListener("storage", onStorage);
    window.addEventListener("carelogix:auth-changed", onCustom);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("carelogix:auth-changed", onCustom);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // When tier override is "free" but the user is authenticated, send no query
    // param so the backend resolves the real persisted tier from the Bearer token.
    const hasAuthToken = (() => {
      try {
        return Boolean(window.localStorage.getItem("carelogix_member_token"));
      } catch { return false; }
    })();
    const tierArg = hasAuthToken && (!tier || tier === "free") ? null : tier;
    Promise.all([
      membershipApi.me(tierArg).then((r) => r.data).catch(() => null),
      membershipApi.tiers().then((r) => r.data).catch(() => null),
    ]).then(([me, tcat]) => {
      if (cancelled) return;
      setProfile(me);
      if (tcat?.tiers) setTiers(tcat.tiers);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [tier, authTick]);

  const setDemoTier = (t) => {
    setTier(t);
    try { window.localStorage.setItem(LS_KEY, t); } catch (_) { /* ignore */ }
  };

  const featureMap = useMemo(() => {
    const m = {};
    (profile?.features || []).forEach((f) => { m[f.key] = f; });
    return m;
  }, [profile]);

  const value = useMemo(() => ({
    tier: profile?.tier || tier,
    tierLabel: profile?.tier_label || tier,
    features: profile?.features || [],
    tiers,
    isLoading: loading,
    allowed: (key) => Boolean(featureMap[key]?.allowed),
    requiredTier: (key) => featureMap[key]?.min_tier || "free",
    setDemoTier,
  }), [profile, tier, tiers, loading, featureMap]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
